<script>
import date from 'locutus/php/datetime/date'
import strtotime from 'locutus/php/datetime/strtotime'
import AdminLayout from '@layouts/admin-main'
import PageNavigation from '@components/admin/page-navigation'
import CsTicketInfo from '@components/admin/cs-ticket-info'
import {
  fetchTicketList,
  closeTicket,
  replyTicket,
  fetchTicketTypes,
} from '@apis/admin/cs'
import fromPairs from 'lodash/fromPairs'
import { mapState } from 'vuex'

export default {
  page: {
    title: 'Admin::CsTicketList',
    meta: [{ name: 'description', content: 'Admin::CsTicketList' }],
  },
  components: { AdminLayout, PageNavigation, CsTicketInfo },
  data() {
    return {
      loading: false,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      s_ticket_seq: 0,
      show_dialog: false,
      types: [],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('cs_ticket_list.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('cs_ticket_list.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadTicketTypes()]).catch((e) =>
        this.$toasted.error(e.message)
      )
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchTicketList({
        ...this.$route.params,
        ...this.$route.query,
      })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadTicketTypes() {
      const { data } = await fetchTicketTypes()
      this.types = data
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = {}
      this.search()
    },
    handleView(seq) {
      this.s_ticket_seq = seq
      this.show_dialog = true
    },
    async handleCloseTicket(seq) {
      try {
        this.loading = true
        if (
          !(await this.$confirm(
            this.$t('cs_ticket_list.msg_close'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await closeTicket(seq)
        await this.init()
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmit(seq) {
      this.init()
      this.s_ticket_seq = seq
    },
    async handleReplyCompleteTicket(seq) {
      try {
        this.loading = true
        if (
          !(await this.$confirm(
            this.$t('cs_ticket_list.msg_answer_complete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await replyTicket(seq)
        await this.init()
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <AdminLayout v-loading.fullscreen="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                :clearable="false"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('cs_ticket_list.above')],
                      ['regdate_less', $t('cs_ticket_list.less_than')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t('cs_ticket_list.statistics_exclude_members')
                }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('admin.regdate')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('cs_ticket_list.inquiry_history') }}</h3>
    <ul :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...types,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.type_seq }"
        :to="{ query: { ...$route.query, type_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>
          {{ title }}
        </a>
      </router-link>
    </ul>

    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.user_id') }}</div></th
          >

          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('cs_ticket_list.inquiry_history') }}</div></th
          >

          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.email_address') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div
              ><a href="#">{{ $t('cs_ticket_list.statea') }}</a></div
            ></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div
              ><a href="#">{{ $t('cs_ticket_list.registration_date') }}</a></div
            ></th
          >

          <th scope="col" rowspan="2"><div>&nbsp;</div></th>
        </tr>
        <tr>
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.user_name') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('cs_ticket_list.cs_inquiry_id') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide" colspan="3"
            ><div>{{ $t('cs_ticket_list.title') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="`${data.seq}-1`" :class="$style[`bg${idx % 2}`]">
            <td rowspan="2" :class="$style.tCenter">{{ data._no }}</td>
            <td v-if="data.user_id && data.user_name" :class="$style.tCenter">{{
              data.user_id
            }}</td>
            <td v-else rowspan="2" :class="$style.tCenter">{{
              $t('cs_ticket_list.non_member')
            }}</td>
            <td :class="$style.tCenter">{{ data.type_title }}</td>
            <td :class="$style.tCenter">{{ data.email_address }}</td>
            <td :class="$style.tCenter">{{
              fromPairs(constants.cs.ticket.status)[data.status]
            }}</td>
            <td :class="$style.tCenter">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>

            <td rowspan="2" :class="$style.tCenter">
              <a
                :class="[$style.button, $style.gray, $style.strong]"
                @click.prevent="handleView(data.seq)"
                ><span>{{ $t('admin_member_list.detail') }}</span></a
              >
              &nbsp;
              <template v-if="data.status === 'PENDING'">
                <a
                  :class="[$style.button, $style.blue]"
                  @click.prevent="handleReplyCompleteTicket(data.seq)"
                  ><span>{{ $t('cs_ticket_list.answer_complete') }}</span></a
                >
                &nbsp;
                <a
                  :class="[$style.button, $style.red]"
                  @click.prevent="handleCloseTicket(data.seq)"
                  ><span>{{ $t('cs_ticket_list.close') }}</span></a
                >
                &nbsp;
              </template>
            </td>
          </tr>
          <tr :key="`${data.seq}-2`" :class="$style[`bg${idx % 2}`]">
            <td v-if="data.user_id && data.user_name" :class="$style.tCenter">{{
              data.user_name
            }}</td>
            <td :class="$style.tCenter">{{ data.ticket_id }}</td>
            <td colspan="3" :class="$style.tCenter">{{ data.title }}</td>
          </tr>
        </template>
        <tr v-if="loading">
          <td colspan="7" :class="$style.tCenter">{{ $t('admin.loading') }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <el-dialog
      :visible.sync="show_dialog"
      :title="$t('cs_ticket_list.contact_request_details')"
    >
      <CsTicketInfo
        :key="s_ticket_seq"
        :v-if="s_ticket_seq"
        :ticket_seq="s_ticket_seq"
        @submit="handleSubmit"
      ></CsTicketInfo>
    </el-dialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
